// headerTokens.js
//
// Requirement 1 (Header Full Customization) reader-side resolvers. Pure helpers
// that map a Template_Theme plus an optional Header override block into the
// concrete background / typography / nav-style tokens the header chrome should
// render.
//
// Precedence for every token:
//   header-specific override  >  cascaded Template_Theme token  >  current default
//
// This mirrors the precedence the footer resolver (`footerTokens.js`) and the
// `--template-*` CSS variables in TemplateRenderer.jsx already use, so the
// builder preview (embedded reader) and the published reader resolve each token
// identically (Builder–Reader Parity).
//
// All inputs are optional. null / undefined / empty-or-whitespace string are
// treated as "no override" so the next fallback wins and an UNSET option
// reproduces today's appearance (additive, non-regression). This module is pure
// and has no side effects. Application to the chrome components is task 8.3 —
// this module only computes tokens.

/**
 * Returns the first argument that is a "present" value, treating
 * null / undefined / empty-or-whitespace string as absent so the next fallback
 * (theme token, then current default) wins.
 *
 * @param {...*} values - candidate values in precedence order
 * @returns {*} the first present value, or undefined when none are present
 */
const firstPresent = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return undefined;
};

/** True for a non-null, non-array plain object. */
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * True when the header's background should resolve to the literal 'transparent'.
 * Honors (a) the normalized `background.transparent === true` override, (b) the
 * legacy `backgroundColor === 'transparent'` string, (c) the legacy
 * `transparent === true` / `headerTheme === 'transparent'` flags, and (d) the
 * existing overlay position behavior (`positionMode === 'overlay'` /
 * `overlay === true`). This preserves today's transparent/overlay rules.
 *
 * @param {object} header
 * @returns {boolean}
 */
const isTransparentHeader = (header) => {
  if (!isObject(header)) return false;
  const background = isObject(header.background) ? header.background : {};
  if (background.transparent === true) return true;
  if (typeof background.color === 'string'
    && background.color.trim().toLowerCase() === 'transparent') return true;
  if (typeof header.backgroundColor === 'string'
    && header.backgroundColor.trim().toLowerCase() === 'transparent') return true;
  if (header.transparent === true) return true;
  if (header.headerTheme === 'transparent') return true;
  if (header.overlay === true) return true;
  if (header.positionMode === 'overlay') return true;
  return false;
};

/**
 * Resolve the header background, applying
 * header-override > existing transparent/overlay behavior > theme.
 *
 * @param {object} [theme] - Template_Theme tokens (attributes.theme)
 * @param {object} [header] - header override block (attributes.header)
 * @returns {{ mode: ('transparent'|'solid'), backgroundColor: string }}
 *   `mode` is 'transparent' when transparent/overlay rules apply, otherwise
 *   'solid'. `backgroundColor` is the literal 'transparent' or the resolved
 *   solid color.
 */
export const resolveHeaderBackground = (theme, header) => {
  const t = isObject(theme) ? theme : {};
  const h = isObject(header) ? header : {};
  const background = isObject(h.background) ? h.background : {};

  // Explicit solid-mode override wins over the legacy transparent/overlay rules.
  const explicitSolid = background.mode === 'solid';

  if (!explicitSolid && isTransparentHeader(h)) {
    return { mode: 'transparent', backgroundColor: 'transparent' };
  }

  const backgroundColor = firstPresent(
    background.color,
    h.backgroundGradient,
    h.backgroundColor,
    t.headerBackground,
    t.background,
  ) || '#2f6f8f';

  return { mode: 'solid', backgroundColor };
};

/**
 * Resolve the header typography tokens, applying
 * header-override > theme token > current default. Every field is `undefined`
 * when neither an override nor a theme token is present so the chrome keeps its
 * current inline defaults (additive / non-regression).
 *
 * @param {object} [theme] - Template_Theme tokens (attributes.theme)
 * @param {object} [header] - header override block (attributes.header)
 * @returns {{
 *   fontFamily: (string|undefined),
 *   fontSize: (string|number|undefined),
 *   fontWeight: (string|number|undefined),
 *   color: (string|undefined),
 *   letterSpacing: (string|number|undefined),
 *   textAlign: (string|undefined)
 * }}
 */
export const resolveHeaderTypography = (theme, header) => {
  const t = isObject(theme) ? theme : {};
  const h = isObject(header) ? header : {};
  const typography = isObject(h.typography) ? h.typography : {};

  return {
    fontFamily: firstPresent(typography.fontFamily, h.fontFamily, t.fontFamily),
    fontSize: firstPresent(typography.fontSize, h.fontSize, t.headerFontSize),
    fontWeight: firstPresent(typography.fontWeight, h.fontWeight, t.headerFontWeight),
    color: firstPresent(typography.color, h.textColor, t.headerText, t.color),
    letterSpacing: firstPresent(typography.letterSpacing, h.letterSpacing),
    textAlign: firstPresent(typography.textAlign, h.textAlign),
  };
};

/**
 * Resolve the header navigation link styling, applying
 * header-override > theme default. Every field is `undefined` when no override
 * or theme token is present so the nav keeps its current default styling.
 *
 * @param {object} [theme] - Template_Theme tokens (attributes.theme)
 * @param {object} [header] - header override block (attributes.header)
 * @returns {{
 *   linkColor: (string|undefined),
 *   hoverColor: (string|undefined),
 *   fontFamily: (string|undefined),
 *   fontSize: (number|string|undefined),
 *   fontWeight: (string|number|undefined),
 *   spacing: (string|number|undefined)
 * }}
 */
export const resolveHeaderNavStyle = (theme, header) => {
  const t = isObject(theme) ? theme : {};
  const h = isObject(header) ? header : {};
  const navStyle = isObject(h.navStyle) ? h.navStyle : {};
  const typography = isObject(h.typography) ? h.typography : {};

  return {
    linkColor: firstPresent(navStyle.linkColor, h.navColor, t.headerText, t.color),
    hoverColor: firstPresent(navStyle.hoverColor, h.navHoverColor, t.primary, t.accent),
    fontFamily: firstPresent(navStyle.fontFamily, typography.fontFamily, h.fontFamily, t.fontFamily),
    fontSize: firstPresent(navStyle.fontSize, h.navFontSize),
    fontWeight: firstPresent(navStyle.fontWeight, h.navFontWeight),
    spacing: firstPresent(navStyle.spacing, h.navGap),
  };
};

const headerTokens = {
  resolveHeaderBackground,
  resolveHeaderTypography,
  resolveHeaderNavStyle,
};

export default headerTokens;
